import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { blogPosts } from "../data/blogData";
import { conditionsData } from "../data/conditionsData";
import { ExternalLink } from "lucide-react";
import { trackEvent } from "../utils/navigate";

const SitemapSection = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-bold text-[#012d5c] mb-4 pb-2 border-b-2 border-[#4caf50]/40">
      {title}
    </h2>
    <ul className="space-y-2">{children}</ul>
  </section>
);

const SitemapLink = ({ to, label, external = false, trackAs }) => (
  <li>
    {external ? (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackAs && trackEvent(trackAs, { entry_point: trackAs === 'login_click' ? 'login' : 'website' })}
        className="inline-flex items-center gap-2 text-[#005eb8] hover:text-[#4caf50] transition-colors text-sm"
      >
        {label}
        <ExternalLink className="w-3 h-3 opacity-60" />
      </a>
    ) : (
      <Link
        to={to}
        className="text-[#005eb8] hover:text-[#4caf50] transition-colors text-sm"
      >
        {label}
      </Link>
    )}
  </li>
);

const SitemapPage = () => {
  useEffect(() => {
    document.title = "Sitemap | Integro Clinics";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content =
      "Navigate the Integro Clinics website. Find all pages including conditions, blog articles, legal information and patient services.";
  }, []);

  const conditions = Object.entries(conditionsData).map(([slug, data]) => ({
    slug,
    title: data.title,
  }));

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero */}
      <div
        className="bg-[#012d5c] py-20"
        style={{
          backgroundImage: "linear-gradient(rgba(1,45,92,0.88),rgba(1,45,92,0.88)),url('https://images.pexels.com/photos/4031818/pexels-photo-4031818.jpeg?auto=compress&cs=tinysrgb&w=1920&h=900&fit=crop')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <span className="inline-block bg-[#4caf50] text-white text-sm font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-5">
            Navigation
          </span>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Sitemap
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto text-lg">
            A complete guide to every page on the Integro Clinics website.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-2">

              {/* Left column */}
              <div>
                <SitemapSection title="Main Pages">
                  <SitemapLink to="/" label="Home" />
                  <SitemapLink to="/about" label="About Integro Clinics" />
                  <SitemapLink to="/how-it-works" label="How It Works" />
                  <SitemapLink to="/transfer-clinic" label="Transfer Clinic" />
                  <SitemapLink to="/faq" label="Frequently Asked Questions" />
                  <SitemapLink to="/blog" label="Blog — Integro News & Education" />
                  <SitemapLink
                    to="https://app.integroclinics.com/v2/registration"
                    label="Check Your Eligibility"
                    external
                    trackAs="eligibility_start"
                  />
                  <SitemapLink
                    to="https://app.integroclinics.com/login"
                    label="Patient Login"
                    external
                    trackAs="login_click"
                  />
                </SitemapSection>

                <SitemapSection title="Legal & Information">
                  <SitemapLink to="/privacy-policy" label="Privacy Policy" />
                  <SitemapLink to="/zero-tolerance-policy" label="Zero Tolerance Policy" />
                  <SitemapLink to="/accessibility-statement" label="Accessibility Statement" />
                  <SitemapLink to="/terms" label="Terms and Conditions" />
                  <SitemapLink to="/complaints" label="Complaints" />
                  <SitemapLink to="/sitemap" label="Sitemap" />
                </SitemapSection>

                <SitemapSection title="Conditions">
                  {conditions.map(({ slug, title }) => (
                    <SitemapLink
                      key={slug}
                      to={`/conditions/${slug}`}
                      label={title}
                    />
                  ))}
                </SitemapSection>
              </div>

              {/* Right column */}
              <div>
                <SitemapSection title="Blog Articles">
                  {blogPosts.map((post) => (
                    <SitemapLink
                      key={post.slug}
                      to={`/blog/${post.slug}`}
                      label={post.title}
                    />
                  ))}
                </SitemapSection>
              </div>

            </div>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SitemapPage;
